import axios from "axios";
import { toast } from "sonner";
import { tryRefresh as tryConsumerRefresh } from "./consumerApi";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const api = axios.create({ baseURL: API });

export function saveFirmAuth(data) {
  if (data?.token) localStorage.setItem("lm_token", data.token);
  if (data?.refresh_token) localStorage.setItem("lm_refresh", data.refresh_token);
}
export function clearFirmAuth() {
  localStorage.removeItem("lm_token");
  localStorage.removeItem("lm_refresh");
}

let mediationCase = null;
let mediationGen = 0;
export const setMediationCase = (id) => { mediationCase = id; mediationGen += 1; };
// Clear after a short delay so cleanup requests fired while leaving a session still go out as the consumer.
// Skipped if the case was (re)activated in the meantime, e.g. the runner effect re-ran.
export const releaseMediationCase = (id) => { const g = mediationGen; setTimeout(() => { if (mediationCase === id && mediationGen === g) mediationCase = null; }, 4000); };
export const getMediationCase = () => mediationCase;

api.interceptors.request.use((config) => {
  if (mediationCase && (config.url || "").startsWith("/platform/mediation")) {
    config.headers.Authorization = `Bearer ${localStorage.getItem("lx_consumer_token") || ""}`;
    config.headers["X-Mediation-Case"] = mediationCase;
    config._medCase = true;
    return config;
  }
  const token = localStorage.getItem("lm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing = null;
async function tryRefresh() {
  const rt = localStorage.getItem("lm_refresh");
  if (!rt) return null;
  if (!refreshing) {
    refreshing = axios
      .post(`${API}/auth/refresh`, { refresh_token: rt })
      .then((r) => { saveFirmAuth(r.data); return r.data.token; })
      .catch(() => { clearFirmAuth(); return null; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    const path = window.location.pathname;
    const onPublicPage =
      path === "/" ||
      path === "/login" ||
      path === "/create" ||
      path === "/x1" ||
      path.startsWith("/lawfirm") ||
      path.endsWith("/portal") ||
      path.endsWith("/admin") ||
      path.startsWith("/accept-invite");
    const isAuthCall = url.includes("/auth/refresh") || url.includes("/auth/login");
    if (cfg._medCase) {
      // Consumer mediation calls: refresh the consumer session and retry once instead of failing silently.
      if (err.response?.status === 401 && !cfg._retry) {
        cfg._retry = true;
        const nt = await tryConsumerRefresh();
        if (nt) return api(cfg);
      }
      return Promise.reject(err);
    }
    if (err.response?.status === 401 && !cfg._retry && !isAuthCall) {
      cfg._retry = true;
      const hadFirm = !!(localStorage.getItem("lm_token") || localStorage.getItem("lm_refresh"));
      const nt = await tryRefresh();
      if (nt) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${nt}`;
        return api(cfg);
      }
      clearFirmAuth();
      // Only firm/platform users get bounced to the platform login; consumers are never redirected here.
      if (hadFirm && !onPublicPage && !url.includes("/auth/me")) window.location.href = "/x1";
    }
    const dt = err.response?.data?.detail;
    // Role-scoped IP restriction tripped server-side (on ANY endpoint). Signal the global
    // IpGuard to replace the UI with the existing Page Not Found screen and reject so the
    // caller's action stops. Token is kept so access auto-restores when the IP is approved.
    if (err.response?.status === 403 && dt && typeof dt === "object" && dt.ip_blocked) {
      try { window.dispatchEvent(new CustomEvent("lx-ip-blocked")); } catch (e) { /* noop */ }
      return Promise.reject(err);
    }
    // A fresh firm/owner account that hasn't enrolled in MFA yet gets 403 {mfa_setup_required}
    // on every gated endpoint. The MfaRequiredGate overlay already handles this UX, so swallow
    // the rejection (never-resolving promise) to avoid noisy "Uncaught 403" runtime overlays.
    if (err.response?.status === 403 && dt && typeof dt === "object" && dt.mfa_setup_required) {
      // Resolve (don't hang or reject): callers get a benign empty payload so awaited
      // navigation continues, and no "Uncaught 403" overlay is raised. The MfaRequiredGate
      // overlays the UI until the user enrolls in 2FA.
      return Promise.resolve({ data: [], status: 403, _mfaSetupRequired: true, config: cfg, headers: {} });
    }
    if (err.response?.status === 403 && typeof dt === "string" && /access required/i.test(dt)) {
      toast.error(dt);
    }
    return Promise.reject(err);
  }
);

export default api;
